import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "../App";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Mic, MicOff, Video, VideoOff, PhoneOff, Users,
  Copy, ScreenShare, ScreenShareOff
} from "lucide-react";

const SAG_LOGO = "/sag-logo.png";

const ICE_SERVERS = { iceServers: [{ urls: "stun:stun.l.google.com:19302" }, { urls: "stun:stun1.l.google.com:19302" }] };

export default function VideoRoom() {
  const { roomCode } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const screenStreamRef = useRef(null);
  const wsRef = useRef(null);
  const makingOffer = useRef(false);

  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [remoteConnected, setRemoteConnected] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [screenSharing, setScreenSharing] = useState(false);
  const [participants, setParticipants] = useState([]);

  // Create peer connection
  const createPeerConnection = useCallback(() => {
    const pc = new RTCPeerConnection(ICE_SERVERS);

    pc.onicecandidate = (event) => {
      if (event.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: "ice-candidate", candidate: event.candidate }));
      }
    };

    pc.ontrack = (event) => {
      if (remoteVideoRef.current && event.streams[0]) {
        remoteVideoRef.current.srcObject = event.streams[0];
        setRemoteConnected(true);
      }
    };

    pc.oniceconnectionstatechange = () => {
      if (pc.iceConnectionState === "disconnected" || pc.iceConnectionState === "failed") {
        setRemoteConnected(false);
      }
    };

    pc.onnegotiationneeded = async () => {
      try {
        makingOffer.current = true;
        await pc.setLocalDescription();
        wsRef.current?.send(JSON.stringify({ type: "offer", sdp: pc.localDescription }));
      } catch (err) {
        console.error("Negotiation error:", err);
      } finally {
        makingOffer.current = false;
      }
    };

    pcRef.current = pc;
    return pc;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Refs are stable, only ICE_SERVERS config matters
  }, []);

  // Handle signaling messages
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Uses refs (pcRef, wsRef) which are stable
  const handleSignaling = useCallback(async (data) => {
    const pc = pcRef.current;
    if (!pc) return;

    try {
      if (data.type === "offer") {
        const offerCollision = makingOffer.current || pc.signalingState !== "stable";
        if (offerCollision) return;
        await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
        await pc.setLocalDescription();
        wsRef.current?.send(JSON.stringify({ type: "answer", sdp: pc.localDescription }));
      } else if (data.type === "answer") {
        await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
      } else if (data.type === "ice-candidate" && data.candidate) {
        await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
      } else if (data.type === "join") {
        setParticipants((prev) => {
          if (!prev.find((p) => p.user_id === data.user_id)) {
            return [...prev, { user_id: data.user_id, user_name: data.user_name }];
          }
          return prev;
        });
      } else if (data.type === "leave") {
        setParticipants((prev) => prev.filter((p) => p.user_id !== data.user_id));
        setRemoteConnected(false);
        if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
      }
    } catch (err) {
      console.error("Signaling error:", err);
    }
  }, []);

  // Setup media + WebSocket + peer connection
  useEffect(() => {
    if (loading || !room) return;

    let cleanup = false;

    const init = async () => {
      // Get local media
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        if (cleanup) { stream.getTracks().forEach(t => t.stop()); return; }
        localStreamRef.current = stream;
        if (localVideoRef.current) localVideoRef.current.srcObject = stream;
        setConnected(true);

        // Create peer connection and add tracks
        const pc = createPeerConnection();
        stream.getTracks().forEach((track) => pc.addTrack(track, stream));

        // Setup WebSocket — use window.location.host for production compatibility
        const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
        const wsHost = window.location.host;
        const ws = new WebSocket(`${protocol}//${wsHost}/api/ws/video/${roomCode}`);

        ws.onopen = () => {
          ws.send(JSON.stringify({ type: "join", user_id: user?.id, user_name: user?.full_name }));
        };
        ws.onmessage = (event) => {
          const msg = JSON.parse(event.data);
          if (msg.user_id !== user?.id) handleSignaling(msg);
        };
        ws.onerror = () => toast.error("Connection error");
        ws.onclose = () => {};
        wsRef.current = ws;
      } catch (err) {
        console.error("Media error:", err);
        toast.error("Failed to access camera/microphone");
      }
    };

    init();

    return () => {
      cleanup = true;
      localStreamRef.current?.getTracks().forEach((t) => t.stop());
      screenStreamRef.current?.getTracks().forEach((t) => t.stop());
      pcRef.current?.close();
      wsRef.current?.close();
    };
  }, [loading, room, roomCode, user, createPeerConnection, handleSignaling]);

  // Fetch room
  useEffect(() => {
    const fetchRoom = async () => {
      try {
        const res = await apiClient.get(`/video-rooms/${roomCode}`);
        setRoom(res.data);
        await apiClient.post(`/video-rooms/${roomCode}/join`);
      } catch {
        toast.error("Failed to join video room");
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };
    fetchRoom();
  }, [roomCode, navigate]);

  const toggleAudio = () => {
    const track = localStreamRef.current?.getAudioTracks()[0];
    if (track) { track.enabled = !track.enabled; setAudioEnabled(track.enabled); }
  };

  const toggleVideo = () => {
    const track = localStreamRef.current?.getVideoTracks()[0];
    if (track) { track.enabled = !track.enabled; setVideoEnabled(track.enabled); }
  };

  const toggleScreenShare = async () => {
    const pc = pcRef.current;
    if (!pc) return;

    if (screenSharing) {
      // Stop screen share, restore camera
      screenStreamRef.current?.getTracks().forEach((t) => t.stop());
      const camTrack = localStreamRef.current?.getVideoTracks()[0];
      if (camTrack) {
        const sender = pc.getSenders().find((s) => s.track?.kind === "video");
        if (sender) await sender.replaceTrack(camTrack);
      }
      if (localVideoRef.current) localVideoRef.current.srcObject = localStreamRef.current;
      setScreenSharing(false);
    } else {
      try {
        const screen = await navigator.mediaDevices.getDisplayMedia({ video: { cursor: "always" } });
        screenStreamRef.current = screen;
        const screenTrack = screen.getVideoTracks()[0];
        const sender = pc.getSenders().find((s) => s.track?.kind === "video");
        if (sender) await sender.replaceTrack(screenTrack);
        if (localVideoRef.current) localVideoRef.current.srcObject = screen;
        setScreenSharing(true);
        screenTrack.onended = () => toggleScreenShare();
      } catch {
        toast.error("Failed to share screen");
      }
    }
  };

  const handleEndCall = async () => {
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    screenStreamRef.current?.getTracks().forEach((t) => t.stop());
    pcRef.current?.close();
    wsRef.current?.send(JSON.stringify({ type: "leave", user_id: user?.id }));
    wsRef.current?.close();
    toast.success("Call ended");
    navigate(-1);
  };

  const copyRoomLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Room link copied to clipboard");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-white animate-pulse">Joining room...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col" data-testid="video-room-page">
      {/* Header */}
      <div className="bg-slate-900/90 backdrop-blur border-b border-slate-800 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <img src={SAG_LOGO} alt="SAG" className="h-8 w-8 object-contain" />
          <div>
            <h1 className="font-heading text-lg font-bold text-white">{room?.title || "Video Call"}</h1>
            <p className="text-sm text-slate-400">Room: {roomCode}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Badge variant="secondary" className={remoteConnected ? "bg-emerald-900 text-emerald-300" : "bg-slate-800 text-slate-300"}>
            <Users className="h-3 w-3 mr-1" />
            {remoteConnected ? "Connected" : `${participants.length + 1} in room`}
          </Badge>
          <Button variant="ghost" size="sm" onClick={copyRoomLink} className="text-slate-300 hover:text-white">
            <Copy className="h-4 w-4 mr-2" /> Copy Link
          </Button>
        </div>
      </div>

      {/* Video Grid */}
      <div className="flex-1 p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-full">
          {/* Local Video */}
          <div className="relative rounded-2xl overflow-hidden bg-slate-900 aspect-video">
            <video ref={localVideoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
            <div className="absolute bottom-4 left-4 bg-slate-900/80 backdrop-blur px-3 py-1.5 rounded-lg">
              <span className="text-sm text-white font-medium">
                {user?.full_name} (You) {screenSharing ? "- Screen" : ""}
              </span>
            </div>
            {!videoEnabled && !screenSharing && (
              <div className="absolute inset-0 bg-slate-800 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full bg-slate-700 flex items-center justify-center mx-auto mb-2">
                    <span className="text-2xl font-bold text-white">{user?.full_name?.charAt(0) || "U"}</span>
                  </div>
                  <span className="text-slate-400">Camera off</span>
                </div>
              </div>
            )}
          </div>

          {/* Remote Video */}
          <div className="relative rounded-2xl overflow-hidden bg-slate-900 aspect-video">
            {remoteConnected ? (
              <>
                <video ref={remoteVideoRef} autoPlay playsInline className="w-full h-full object-cover" />
                <div className="absolute bottom-4 left-4 bg-slate-900/80 backdrop-blur px-3 py-1.5 rounded-lg">
                  <span className="text-sm text-white font-medium">{participants[0]?.user_name || "Participant"}</span>
                </div>
              </>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-24 h-24 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-4 animate-pulse">
                    <Users className="h-10 w-10 text-slate-600" />
                  </div>
                  <p className="text-slate-400 mb-2">Waiting for others to join...</p>
                  <p className="text-slate-500 text-sm">Share the room link to invite participants</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="p-6 flex justify-center">
        <div className="bg-slate-900/90 backdrop-blur rounded-full px-8 py-4 flex items-center gap-4 border border-slate-800">
          <Button variant="ghost" size="icon" onClick={toggleAudio}
            className={`rounded-full w-12 h-12 ${audioEnabled ? "bg-slate-800 text-white hover:bg-slate-700" : "bg-red-500 text-white hover:bg-red-600"}`}
            data-testid="toggle-audio">
            {audioEnabled ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
          </Button>
          <Button variant="ghost" size="icon" onClick={toggleVideo}
            className={`rounded-full w-12 h-12 ${videoEnabled ? "bg-slate-800 text-white hover:bg-slate-700" : "bg-red-500 text-white hover:bg-red-600"}`}
            data-testid="toggle-video">
            {videoEnabled ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
          </Button>
          <Button variant="ghost" size="icon" onClick={toggleScreenShare}
            className={`rounded-full w-12 h-12 ${screenSharing ? "bg-blue-500 text-white hover:bg-blue-600" : "bg-slate-800 text-white hover:bg-slate-700"}`}
            data-testid="toggle-screen-share">
            {screenSharing ? <ScreenShareOff className="h-5 w-5" /> : <ScreenShare className="h-5 w-5" />}
          </Button>
          <div className="w-px h-8 bg-slate-700" />
          <Button variant="destructive" size="icon" onClick={handleEndCall}
            className="rounded-full w-14 h-14 bg-red-500 hover:bg-red-600" data-testid="end-call">
            <PhoneOff className="h-6 w-6" />
          </Button>
        </div>
      </div>
    </div>
  );
}
